import { evaluateCondition } from '../../utils/field_type_operators.utils';
export const getColumnStyle = (columnName, columnsStyles) => columnsStyles?.find((style) => style.columnName === columnName);
export const getConditionalStyleValue = (value, columnStyle) => {
    // If conditions array is defined (even if empty), use defaultValue as fallback
    if (columnStyle.conditions && Array.isArray(columnStyle.conditions)) {
        for (const cond of columnStyle.conditions) {
            const requiresValue = cond.operator !== 'set' && cond.operator !== 'notSet';
            // Skip conditions with empty/missing comparison values, unless the operator
            // itself has no value (e.g. `set` / `notSet` — blank checks).
            if (requiresValue && (cond.value === undefined || cond.value === null || cond.value === '')) {
                continue;
            }
            const isMatch = evaluateCondition(value, String(cond.operator), { singleValue: cond.value, from: cond.value, to: cond.endValue }, cond.fieldType);
            if (isMatch) {
                return cond.result;
            }
        }
        // If no condition matched (or conditions is empty), return the defaultValue
        return columnStyle.defaultValue ?? '';
    }
    // Fallback to the single-condition legacy shape.
    if (columnStyle.condition) {
        const isConditionMet = evaluateCondition(value, String(columnStyle.condition.operator), {
            singleValue: columnStyle.condition.value,
            from: columnStyle.condition.value,
            to: columnStyle.condition.endValue,
        }, columnStyle.condition.fieldType);
        return isConditionMet ? (columnStyle.trueValue ?? '') : (columnStyle.falseValue ?? '');
    }
    // If no styling information is provided, return an empty string.
    return '';
};
export const getDynamicStyles = (item, style) => {
    if (Array.isArray(style)) {
        // Check if this is a ColumnStyle array (has conditions) vs ArrayStyleConfig array (has value)
        const isColumnStyleArray = style.length > 0 && 'conditions' in style[0];
        if (isColumnStyleArray) {
            // Handle ColumnStyle[] - conditional styling per column
            const columnStyles = style;
            const result = {};
            for (const columnStyle of columnStyles) {
                if (!columnStyle.columnName || !columnStyle.property) {
                    continue;
                }
                // Check if the series key matches the columnName (for per-bar coloring based on values).
                // For split-by / grouped charts the series key is a composite like
                // "DimensionValue,Table.measure" so we also accept a suffix match after a comma.
                const keyMatchesColumn = item.key === columnStyle.columnName ||
                    (typeof item.key === 'string' && item.key.split(',').some((part) => part === columnStyle.columnName));
                if (keyMatchesColumn && item.series) {
                    // Return an array of colors, one for each data point
                    const colors = item.series.map(({ value }) => {
                        const numValue = typeof value === 'string' ? parseFloat(value) : value;
                        return getConditionalStyleValue(numValue, columnStyle);
                    });
                    result[columnStyle.property] = colors;
                    continue;
                }
                // Extract dimension value from series key
                // For stacked charts, item.key might be like "1,Scoring_Reactogenicity_Diary_Report.occurence_with_drilldown"
                // We need to find the part that matches our columnName
                let dimensionValue = null;
                if (item.key) {
                    // Split the key and find the dimension value
                    const keyParts = item.key.split(',');
                    // Try to find the dimension value
                    // First check if any part directly matches the column name pattern
                    for (const part of keyParts) {
                        if (part.includes(columnStyle.columnName.split('.').pop() || '')) {
                            dimensionValue = part;
                            break;
                        }
                    }
                    // If not found, try to match against the first part (often the dimension value)
                    if (dimensionValue === null) {
                        // For grade dimension, the first part is often the grade value
                        dimensionValue = keyParts[0];
                        // Try to convert to number if it looks like a number
                        if (!isNaN(Number(dimensionValue))) {
                            dimensionValue = Number(dimensionValue);
                        }
                    }
                }
                if (dimensionValue !== null) {
                    const styleValue = getConditionalStyleValue(dimensionValue, columnStyle);
                    if (styleValue) {
                        result[columnStyle.property] = styleValue;
                    }
                }
            }
            return result;
        }
        // Handle ArrayStyleConfig[] - direct mapping
        const arrayConfigs = style;
        return arrayConfigs.reduce((acc, styleConfig) => {
            if (item.key === styleConfig.columnName) {
                acc[styleConfig.property] = styleConfig.value;
            }
            return acc;
        }, {});
    }
    // Handle existing conditional style
    if (item.series) {
        return item.series.map(({ value }) => getConditionalStyleValue(value, style));
    }
    // Handle empty object case (for bubble charts)
    return {};
};
